import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'

import { sendAnalyticsEvent } from 'services/analytics'
import { initBranch } from 'services/branch'
import { initIntercom } from 'services/Intercom'

import generalActions from 'models/General/actions'
import generalSelectors from 'models/General/selectors'
import authActions from 'models/Authentication/actions'
import authSelectors from 'models/Authentication/selectors'
import profileActions from 'models/ProfileInfo/actions'
import rewardsActions from 'models/Rewards/actions'

import { EVENT_TYPE } from 'resources'
import { APP_URLS } from 'resources/urls'

import fetchIpAddress from 'utils/ip-address'

import SpinnerScreen from 'screens/SpinnerScreen'

const InitializeApp = ({ children }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const region = useSelector(generalSelectors.getRegion)
  const isAppInitialized = useSelector(generalSelectors.getIsAppInitialized)
  const isRegionCheckFailure = useSelector(generalSelectors.getIsRegionCheckFailure)
  const isSecureConstantsFetched = useSelector(generalSelectors.getIsSecureConstantsFetched)

  const isAuthInitialized = useSelector(authSelectors.getIsAuthInitialized)
  const isAuthChecked = useSelector(authSelectors.getIsAuthChecked)
  const isAuthenticated = useSelector(authSelectors.getIsAuthenticated)
  const isSigningOut = useSelector(authSelectors.getIsSigningOut)

  useEffect(() => {
    dispatch(generalActions.initApp.init())
    initBranch()
    initIntercom()
  }, [dispatch])

  useEffect(() => {
    if (isRegionCheckFailure && location.pathname !== APP_URLS.NETWORK_ERROR) {
      history.replace(APP_URLS.NETWORK_ERROR)
    }
  }, [history, location, dispatch, isRegionCheckFailure])

  useEffect(() => {
    if (isSigningOut) {
      return
    }
    if (isAppInitialized && !!region) {
      dispatch(authActions.initializeAuth.init())
    }
  }, [dispatch, isSigningOut, isAppInitialized, region])

  useEffect(() => {
    if (isSigningOut) {
      return
    }
    if (isAuthInitialized && !isAuthChecked) {
      dispatch(authActions.checkIsAuthenticated.init())
    }
  }, [dispatch, isSigningOut, isAuthInitialized, isAuthChecked])

  useEffect(() => {
    dispatch(generalActions.fetchConstants.init())
  }, [dispatch])

  useEffect(() => {
    if (isSigningOut) {
      return
    }
    if (isAuthenticated && !isSecureConstantsFetched) {
      dispatch(generalActions.fetchSecureConstants.init())
    }
  }, [dispatch, isSigningOut, isAuthenticated, isSecureConstantsFetched])

  useEffect(() => {
    if (isSigningOut) {
      return
    }

    const upsertIpAddress = async () => {
      const { ipAddress, country, countryRegion } = await fetchIpAddress()
      dispatch(profileActions.upsertIpAddress.init(ipAddress, country, countryRegion))
      sendAnalyticsEvent(EVENT_TYPE.appInit, { country, countryRegion })
    }

    if (isAuthenticated) {
      dispatch(profileActions.fetchProfileInfo.init())
      dispatch(rewardsActions.fetchAll.init())
      upsertIpAddress()
    }
  }, [dispatch, isSigningOut, isAuthenticated])

  if (!isSigningOut && (!isAppInitialized || !isAuthInitialized || !isAuthChecked)) {
    return <SpinnerScreen />
  }

  return children
}

InitializeApp.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
}

export default InitializeApp
